import { AlertColor } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import Notification from '../../../../../../../components/Notification';
import { PriceOverrideCards } from '../PriceOverrideCards';
import {
  applyPrioritiesFromOrder,
  createEmptyPriceOverrideRow,
  DropdownOption,
  PriceOverrideRow,
  reorderPriceOverrides,
  validatePriceOverrideRow,
} from '../priceFormUtils';

type AddEditPriceOverRideProps = {
  editedData: PriceOverrideRow[];
  setData: Dispatch<SetStateAction<PriceOverrideRow[]>>;
  dropdownValue: any;
};

export const AddEditPriceOverRide: React.FC<AddEditPriceOverRideProps> = ({
  editedData = [],
  setData,
  dropdownValue,
}) => {
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const packageOptions: DropdownOption[] = useMemo(
    () => dropdownValue?.pricePackage ?? dropdownValue?.pricePackageId ?? [],
    [dropdownValue],
  );

  const weekDayOptions: DropdownOption[] = useMemo(
    () => dropdownValue?.weekDays ?? [],
    [dropdownValue],
  );

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const setErrorForIndex = useCallback((index: number, row: PriceOverrideRow) => {
    const error = validatePriceOverrideRow(row);
    setErrors(prev => {
      const next = { ...prev };
      if (error) next[index] = error;
      else delete next[index];
      return next;
    });
    return error;
  }, []);

  const patchRow = useCallback(
    (index: number, patch: Partial<PriceOverrideRow>) => {
      if ('priority' in patch) return;
      setData(prev => {
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        setErrorForIndex(index, next[index]);
        return next;
      });
    },
    [setErrorForIndex],
  );

  const handleChange = (index: number, field: keyof PriceOverrideRow, value: any) => {
    if (field === 'priority' || field === 'weekDayNames') return;
    patchRow(index, { [field]: value });
  };

  const handlePackageSelect = (index: number, option: DropdownOption | undefined) => {
    if (!option) {
      patchRow(index, { pricePackage: undefined, pricePackageId: undefined });
      return;
    }
    patchRow(index, {
      pricePackage: { value: option.value, label: option.label },
      pricePackageId: option.value,
    });
  };

  const handleWeekDaysChange = (index: number, weekDays: DropdownOption[]) => {
    patchRow(index, {
      weekDays,
      weekDayNames: weekDays.map(d => d.label).join(', '),
    });
  };

  const handleDelete = (index: number) => {
    setData(applyPrioritiesFromOrder(editedData.filter((_, i) => i !== index)));
    setErrors({});
  };

  const handleAdd = () => {
    setData(applyPrioritiesFromOrder([...editedData, createEmptyPriceOverrideRow()]));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    setData(reorderPriceOverrides(editedData, fromIndex, toIndex));
    setErrors({});
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <PriceOverrideCards
        rows={editedData}
        packageOptions={packageOptions}
        weekDayOptions={weekDayOptions}
        errors={errors}
        onChange={handleChange}
        onPackageSelect={handlePackageSelect}
        onWeekDaysChange={handleWeekDaysChange}
        onDelete={handleDelete}
        onAdd={handleAdd}
        onReorder={handleReorder}
      />
    </>
  );
};
